/** @jsxImportSource theme-ui @jsxRuntime classic */
import React from 'react';
import { useWindowSize } from 'usehooks-ts';

import {
  Box,
  Flex,
  CarouselV2,
  CarouselNavigationV2,
  CarouselV2ContextProvider,
  useResponsiveValue,
} from '..';

import {
  SocialCarouselCard,
  SocialCarouselCardProps,
} from './SocialCarouselCard';

export interface SocialCarouselProps {
  cards: SocialCarouselCardProps[];
  onActiveCardChange?: (id: string | null) => void;
  desktopCardsPaddingLeft?: string;
  mobileContainerFullWidth?: boolean;
  carouselVariant?: 'default' | 'private-events';
}

export function SocialCarousel({
  cards,
  onActiveCardChange,
  carouselVariant,
}: SocialCarouselProps): React.JSX.Element {
  const leftSpacing = useCarouselLeftSpacing();

  return (
    <Box data-module="SocialCarousel">
      <Flex sx={{ flexDirection: 'column' }}>
        <CarouselV2ContextProvider>
          <CarouselNavigationV2
            containerStyle={{
              marginBottom: '40px',
              marginTop: '40px',
              marginLeft: leftSpacing,
              display: 'flex',
            }}
          />

          <CarouselV2
            slideStyle={{ maxWidth: 356 }}
            swiperOptions={{
              spaceBetween: 0,
              style: { paddingLeft: leftSpacing },
            }}
          >
            {cards.map((card) => (
              <div data-testid="carousel-item" key={card.caption}>
                <SocialCarouselCard
                  {...card}
                  carouselVariant={carouselVariant}
                  onClick={() => onActiveCardChange?.(card.id)}
                />
              </div>
            ))}
          </CarouselV2>
        </CarouselV2ContextProvider>
      </Flex>
    </Box>
  );
}

export const useCarouselLeftSpacing = (
  leftSpacingOffsets = [0, 0, 32, 32, 32],
  maxWidth = 1200,
): number => {
  const { width } = useWindowSize();
  const activeBreakpointSize = useResponsiveValue([
    width,
    640,
    832,
    1024,
    1200,
  ]);
  const leftSpacingOffset = useResponsiveValue(leftSpacingOffsets);

  const largeWidth = width >= maxWidth;

  const leftSpacing = largeWidth
    ? (width - activeBreakpointSize) / 2 + leftSpacingOffset
    : leftSpacingOffset;

  return leftSpacing;
};
